import {css, useTheme} from '@mui/material';
import {
  asQueryString,
  ConfigObjectType,
  DlMuiButton,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  LoadingSpinner,
  mixins,
  omitNullsy,
  omitPredicate,
  useDefaultScrollTo,
  useLocalStorage,
  useLocationQuery,
} from '@wallaby-ui/core';
import {LightSelectedFilters, TableSorting, useFormatters, usePermissions, UserFilterDto, useUserFilters} from '@wallaby-ui/document';
import {isEmpty, isEqual} from 'lodash';
import * as React from 'react';
import {useCallback, useMemo, useState} from 'react';
import {Helmet} from 'react-helmet';
import {Link} from 'react-router-dom';

import {DEFAULT_HOSPITAL_REPORT_SORTING, FILTER_DEPARTMENT_TABLE_CHART_LAYOUT_SPLIT_CONFIG} from './meriehospitalreports';
import {DepartmentReportBasicMeasuresTable} from './meriehospitalreports/DepartmentReportBasicMeasuresTable';
import {
  getTableDefaultSorting,
  getTableSortingProp,
  getTableSortingUpdate,
  MemoizedDepartmentReportChartTable,
} from './meriehospitalreports/DepartmentReportChartTable';
import {DepartmentReportFilters, normalizeInterventionsDepartmentReportFiltersForCompare} from './meriehospitalreports/DepartmentReportFilters';
import {
  DEFAULT_BASIC_MEASURES_TABLE_SORTING,
  DEFAULT_DEPARTMENT_REPORT_FILTERS,
  FiltersData,
  getMainSplitLayoutId,
  InterventionsDepartmentReportFilter,
  ReportType,
} from './meriehospitalreports/departmenttypes';
import {useDepartmentReportData} from './meriehospitalreports/useDepartmentReportData';
import {useDepartmentReportExport} from './meriehospitalreports/useDepartmentReportExport';
import {useHospitalOptions} from './meriehospitalreports/useHospitalOptions';
import {useResetDepartmentReportLayout} from './meriehospitalreports/useResetDepartmentReportLayout';
import {FiltersData as PhRecFiltersData} from '../PhRecommendationListLayout';

type Props = {
  config: ConfigObjectType;
};

export const MerieDepartmentReportLayout: React.FC<Props> = (props) => {
  const {config} = props;
  const storageIdPrefix = '/merieDepartmentReportLayout';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);

  const {firstHospitalOption} = useHospitalOptions(config);
  const defaultReportFilters: FiltersData['report'] = useMemo(() => {
    return DEFAULT_DEPARTMENT_REPORT_FILTERS(firstHospitalOption?.id);
  }, [firstHospitalOption?.id]);

  const filters: FiltersData = useMemo(
    () => ({
      report: query?.report ?? defaultReportFilters,
      basicMeasuresTableSorting: query?.reportTableSorting ?? DEFAULT_BASIC_MEASURES_TABLE_SORTING,
      riskCategoryTableSorting: query?.riskCategoryTableSorting ?? getTableDefaultSorting(ReportType.RISK_CATEGORY),
      drpsPerAtcTableSorting: query?.drpsPerAtcTableSorting ?? getTableDefaultSorting(ReportType.DRPS_PER_ATC),
      typesOfProblemTableSorting: query?.typesOfProblemTableSorting ?? getTableDefaultSorting(ReportType.TYPES_OF_PROBLEMS),
      causesOfProblemTableSorting: query?.causesOfProblemTableSorting ?? getTableDefaultSorting(ReportType.CAUSES_OF_PROBLEMS),
      plannedInterventionsTableSorting: query?.plannedInterventionsTableSorting ?? getTableDefaultSorting(ReportType.PLANNED_INTERVENTIONS),
      savedFiltersOpen: query?.savedFiltersOpen ?? true,
    }),
    [query, defaultReportFilters]
  );

  const changeRecommendationFilters = useCallback((newFilters) => changeFilters({recommendation: newFilters}), [changeFilters]);

  const component = 'departmentReport';
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: changeRecommendationFilters,
  });

  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;

  return (
    <MerieDepartmentReportLayoutComponents
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
      defaultReportFilters={defaultReportFilters}
    />
  );
};

type MerieDepartmentReportLayoutComponentsProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Partial<FiltersData>) => void;
  defaultReportFilters?: FiltersData['report'];
};

const MerieDepartmentReportLayoutComponents: React.FC<MerieDepartmentReportLayoutComponentsProps> = ({
  config,
  storageIdPrefix,
  component,
  userFiltersLoading,
  addedUserFilters,
  userFilters,
  changeUserFilter,
  filters,
  changeFilters,
  defaultReportFilters,
}) => {
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const {isLoading: permissionsLoading} = usePermissions(config);
  const formatters = useFormatters();
  const {t} = formatters;
  const [rerenderOnLayoutResetId, setRerenderOnLayoutResetId] = useState(0);

  const changeFiltersRef = React.useRef(changeFilters);
  React.useEffect(() => {
    changeFiltersRef.current = changeFilters;
  }, [changeFilters]);

  const reportUrl = `${config.service.url.interventionDepartmentReport}`;
  const {reportData, isLoading: isReportDataLoading} = useDepartmentReportData({
    reportUrl,
    filters: omitPredicate(filters.report, (val: any) => isEmpty(val)),
  });

  const {handleXlsxExport, warningDialog} = useDepartmentReportExport(filters.report, config, reportData?.basicMeasures ? 1 : 0);

  const searchQueryParams: Pick<PhRecFiltersData, 'recommendation'> = useMemo(() => {
    return {
      recommendation: {
        pharmaRecommendationDateFrom: filters.report.dateFrom,
        pharmaRecommendationDateTo: filters.report.dateTo,
        pharmaRecommendationOrganizationIds: filters.report.organizationIds,
        riskiness: filters.report.riskCategories,
        drpFilter: {
          causes: filters.report.causes,
          manifestation: filters.report.manifestations?.[0],
          manifestations: filters.report.manifestations,
          medicationAtcId: filters.report.atcHierarchyIds,
          plannedInterventions: filters.report.plannedInterventions,
          types: filters.report.types,
        },
      },
    };
  }, [
    filters.report.atcHierarchyIds,
    filters.report.causes,
    filters.report.dateFrom,
    filters.report.dateTo,
    filters.report.manifestations,
    filters.report.organizationIds,
    filters.report.plannedInterventions,
    filters.report.riskCategories,
    filters.report.types,
  ]);

  const handleFiltersChange = useCallback(
    (newFilters: Partial<InterventionsDepartmentReportFilter>) => {
      changeFiltersRef.current?.({
        report: {...filters.report, ...newFilters},
      });
    },
    [filters.report]
  );

  const handleBasicMeasuresSortingChange = useCallback((sorting: TableSorting) => {
    changeFiltersRef.current?.({basicMeasuresTableSorting: sorting});
  }, []);

  const handleBasicMeasuresSortingReset = useCallback(() => {
    changeFiltersRef.current?.({basicMeasuresTableSorting: DEFAULT_HOSPITAL_REPORT_SORTING});
  }, []);

  const handleReportTableSortingChange = useCallback((reportType: ReportType, newSorting: TableSorting) => {
    const updateObject = getTableSortingUpdate(reportType, newSorting);
    changeFiltersRef.current?.(updateObject);
  }, []);

  const onToggleSavedFiltersOpen = useCallback(() => {
    changeFiltersRef.current?.({savedFiltersOpen: !filters.savedFiltersOpen});
  }, [filters.savedFiltersOpen]);

  const handleResetFilters = useCallback(() => {
    changeFiltersRef.current?.({report: defaultReportFilters});
  }, [defaultReportFilters]);

  const handleSetFilter = useCallback((newFilter: InterventionsDepartmentReportFilter) => {
    changeFiltersRef.current?.({report: newFilter});
  }, []);

  const hasFilters = !isEqual(
    normalizeInterventionsDepartmentReportFiltersForCompare(omitNullsy(filters.report) as any),
    normalizeInterventionsDepartmentReportFiltersForCompare(omitNullsy(defaultReportFilters) as any)
  );
  const mainSplitLayout = FILTER_DEPARTMENT_TABLE_CHART_LAYOUT_SPLIT_CONFIG(getMainSplitLayoutId());
  const onLayoutResetCompleted = useCallback(() => setRerenderOnLayoutResetId((val) => val + 1), []);
  const {resetLayout} = useResetDepartmentReportLayout(onLayoutResetCompleted);

  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.scroll`, 0);
  const scrollWrapperRef = useDefaultScrollTo(defaultScroll);
  const onScroll = useCallback(
    (event: any) => {
      setDefaultScroll((event.target as HTMLElement).scrollTop);
    },
    [setDefaultScroll]
  );

  if (permissionsLoading) {
    return <LoadingSpinner isVisible text='' />;
  }

  return (
    <div css={muiStyles.contentWrapper} style={{overflowY: 'auto'}} ref={scrollWrapperRef} onScroll={onScroll}>
      <Helmet title={`${t('reports.list.pageTitle')} | ${t('navigation.route.departmentReportMerie')}`} />
      <LightSplitLayout
        key={`department-report-layout-${rerenderOnLayoutResetId}`}
        initSizes={mainSplitLayout[0].sizes}
        minSizes={mainSplitLayout[0].minSize}
        storageId={mainSplitLayout[0].layoutId}
        isVertical
        gutterSize={18}
        noGrow={[!filters.savedFiltersOpen, false, false, false, false, false, false]}
        autoFitFilterRows={{enabled: filters.savedFiltersOpen}}
        hidden={[false, false, false, false, false, false, false]}
        style={{height: '3200px'}}
      >
        <div data-name='report-filters' css={muiStyles.filtersContentBox}>
          <DepartmentReportFilters
            filters={filters.report}
            onFiltersChange={handleFiltersChange}
            savedFiltersOpen={filters.savedFiltersOpen}
            onToggleSavedFilters={onToggleSavedFiltersOpen}
            storageIdPrefix={storageIdPrefix}
            component={component}
            userFiltersLoading={userFiltersLoading}
            addedUserFilters={addedUserFilters}
            userFilters={userFilters}
            changeUserFilter={changeUserFilter}
            config={config}
            onSetFilter={handleSetFilter}
            defaultReportFilters={defaultReportFilters}
            hasActiveFilter={hasFilters}
          />
        </div>
        <div data-name='report-table' css={muiStyles.contentBox}>
          <IconHeaderWithSearch
            iconName='listIconMultiColor'
            label='department.report.basicmeasures.table.name'
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
            childrenWrapperStyle={{gap: 6}}
          >
            <LightSelectedFilters
              align='flex-start'
              marginLeft='8px'
              hasFilters={hasFilters}
              handleResetFilters={handleResetFilters}
              hasSorting={!isEqual(filters.basicMeasuresTableSorting, DEFAULT_HOSPITAL_REPORT_SORTING)}
              handleResetSorting={handleBasicMeasuresSortingReset}
              hasXlsxExport
              handleXlsxExport={handleXlsxExport}
            />
            <DlMuiButton
              type='chip-filled'
              title='department.report.resetLayout'
              tooltip='department.report.resetLayout.tooltip'
              onClick={resetLayout}
            />
            <Link css={muiStyles.showAllLink} tabIndex={0} to={`/pharmacotherapyRecommendationList?${asQueryString(searchQueryParams)}`}>
              <DlMuiButton
                type='chip-filled'
                title='dashboard.showAll'
                onClick={() => {
                  // noop
                }}
              />
            </Link>
          </IconHeaderWithSearch>
          <DepartmentReportBasicMeasuresTable
            data={reportData?.basicMeasures}
            isLoading={isReportDataLoading}
            sorting={filters.basicMeasuresTableSorting}
            onSortingChange={handleBasicMeasuresSortingChange}
            filters={filters}
            storageIdPrefix={storageIdPrefix}
          />
          {warningDialog}
        </div>

        <div data-name='report-charts' css={muiStyles.chartsContentBox}>
          <MemoizedDepartmentReportChartTable
            key={`department-report-layout-charttable-${rerenderOnLayoutResetId}`}
            reportType={ReportType.RISK_CATEGORY}
            data={reportData}
            isLoading={isReportDataLoading}
            sorting={getTableSortingProp(ReportType.RISK_CATEGORY, filters)}
            onSortingChange={handleReportTableSortingChange}
            storageIdPrefix={storageIdPrefix}
            chartValueSelection={filters.report.chartValueSelection}
            chartTypeSelection={filters.report.chartTypeSelection}
            config={config}
          />
        </div>

        <div data-name='report-charts' css={muiStyles.chartsContentBox}>
          <MemoizedDepartmentReportChartTable
            key={`department-report-layout-charttable-${rerenderOnLayoutResetId}`}
            reportType={ReportType.DRPS_PER_ATC}
            data={reportData}
            isLoading={isReportDataLoading}
            sorting={getTableSortingProp(ReportType.DRPS_PER_ATC, filters)}
            onSortingChange={handleReportTableSortingChange}
            storageIdPrefix={storageIdPrefix}
            chartValueSelection={filters.report.chartValueSelection}
            chartTypeSelection={filters.report.chartTypeSelection}
            config={config}
          />
        </div>

        <div data-name='report-charts' css={muiStyles.chartsContentBox}>
          <MemoizedDepartmentReportChartTable
            key={`department-report-layout-charttable-${rerenderOnLayoutResetId}`}
            reportType={ReportType.TYPES_OF_PROBLEMS}
            data={reportData}
            isLoading={isReportDataLoading}
            sorting={getTableSortingProp(ReportType.TYPES_OF_PROBLEMS, filters)}
            onSortingChange={handleReportTableSortingChange}
            storageIdPrefix={storageIdPrefix}
            chartValueSelection={filters.report.chartValueSelection}
            chartTypeSelection={filters.report.chartTypeSelection}
            config={config}
          />
        </div>

        <div data-name='report-charts' css={muiStyles.chartsContentBox}>
          <MemoizedDepartmentReportChartTable
            key={`department-report-layout-charttable-${rerenderOnLayoutResetId}`}
            reportType={ReportType.CAUSES_OF_PROBLEMS}
            data={reportData}
            isLoading={isReportDataLoading}
            sorting={getTableSortingProp(ReportType.CAUSES_OF_PROBLEMS, filters)}
            onSortingChange={handleReportTableSortingChange}
            storageIdPrefix={storageIdPrefix}
            chartValueSelection={filters.report.chartValueSelection}
            chartTypeSelection={filters.report.chartTypeSelection}
            config={config}
          />
        </div>

        <div data-name='report-charts' css={muiStyles.chartsContentBox}>
          <MemoizedDepartmentReportChartTable
            key={`department-report-layout-charttable-${rerenderOnLayoutResetId}`}
            reportType={ReportType.PLANNED_INTERVENTIONS}
            data={reportData}
            isLoading={isReportDataLoading}
            sorting={getTableSortingProp(ReportType.PLANNED_INTERVENTIONS, filters)}
            onSortingChange={handleReportTableSortingChange}
            storageIdPrefix={storageIdPrefix}
            chartValueSelection={filters.report.chartValueSelection}
            chartTypeSelection={filters.report.chartTypeSelection}
            config={config}
          />
        </div>
      </LightSplitLayout>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  contentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
  `,
  /* Filters card only: no bottom padding, so the split's autoFit "half of the 3rd filter row" peek is
     clipped right AT the divider instead of 16px above it (the padding pushed the clip line up). */
  filtersContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
    overflow-y: auto;
  `,
  chartsContentBox: css`
    ${mixins.contentBox};
    padding: 0;
    row-gap: 16px;
    overflow-y: auto;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  resetStylesButton: css`
    margin-right: 15px;
  `,
});
